class PreBuyUtils {
  constructor () {
    this.accessToken = window.theme.preBuy.token
    this.shopDomain = window.Shopify.shop
    this.currentMarket = window.theme.market

    // Will be populated from shop metafield
    this.marketConfig = null

    // Cache for API responses
    this.cache = {}
    this.cacheExpiry = 30000 // 30 seconds
  }

  /**
   * A custom BAO method that runs through the relevant methods below, capturing
   * all the relevant data into an easy-to-use object that is then returned.
   *
   * @returns {Promise<Object>}
   */
  async getPreBuyData (productId, currentVariantId) {
    const data = {}

    if (!window.theme.preBuy.enabled) {
      return data
    }

    try {
      const productData = await this.fetchStorefrontData(productId)
      if (!productData) {
        return data
      }

      const variant = this.findCurrentVariant(productData, currentVariantId)
      if (!variant) {
        return data
      }

      const pools = this.getPreorderPools(variant)
      const pool = pools ? this.findMarketPool(pools) : null

      data.enabled = variant.quantityAvailable <= 0
      data.quantityAvailable = variant.quantityAvailable
      data.currentlyNotInStock = variant.currentlyNotInStock

      if (!pool) {
        return data
      }

      // Assign additional values
      data.variantPool = pool

      if (pool?.estDispatchDate) {
        data.variantPool.estDispatchDateFormatted = this.formatDate(
          new Date(pool.estDispatchDate),
        )
      }

      return data
    } catch (error) {
      console.error('Error retrieving data:', error)
    }
  }

  /**
   * Fetch product and market data from Storefront API
   * Includes both variant data and shop-level market configuration
   * @returns {Promise<Object>} The API response data
   */
  async fetchStorefrontData (productId) {
    const cacheKey = `product_${productId}`

    // Check cache first
    if (
      this.cache[cacheKey] &&
      this.cache[cacheKey].timestamp > Date.now() - this.cacheExpiry
    ) {
      return this.cache[cacheKey].data
    }

    // GraphQL query that fetches both product data and market configuration
    const query = `
      query getProductAndMarketData($productId: ID!) @inContext(country:${window.theme.marketCountry.toUpperCase()}) {
        product(id: $productId) {
          variants(first: 50) {
            edges {
              node {
                id
                availableForSale
                quantityAvailable
                currentlyNotInStock
                metafield(namespace: "prebuy", key: "campaigns") {
                  value
                }
              }
            }
          }
        }
        # Fetch market configuration from shop metafield
        shop {
          metafield(namespace: "prebuy", key: "markets") {
            value
          }
        }
      }
    `

    try {
      // Use latest API version for future compatibility
      const response = await fetch(
        `https://${this.shopDomain}/api/2025-01/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.accessToken,
          },
          body: JSON.stringify({
            query,
            variables: {
              productId: `gid://shopify/Product/${productId}`,
            },
          }),
        },
      )

      const json = await response.json()

      // Process market configuration from shop metafield
      if (json.data?.shop?.metafield?.value) {
        try {
          // Parse and store market configuration for use in pool selection
          const parsedConfig = JSON.parse(json.data.shop.metafield.value)

          // Only use the parsed config if it's valid
          if (
            parsedConfig &&
            typeof parsedConfig === 'object' &&
            Object.keys(parsedConfig).length > 0
          ) {
            this.marketConfig = parsedConfig
            // Update debug panel with market config
            if (this.debugMarketConfig) {
              this.debugMarketConfig.textContent = JSON.stringify(
                parsedConfig,
                null,
                2,
              )
            }
          } else {
            this.marketConfig = null
            // Update debug panel
            if (this.debugMarketConfig) {
              this.debugMarketConfig.textContent = 'Invalid or empty config'
            }
          }
        } catch (e) {
          console.error('Error parsing market config from metafield:', e)
          this.marketConfig = null
          // Update debug panel
          if (this.debugMarketConfig) {
            this.debugMarketConfig.textContent = 'Error parsing config'
          }
        }
      } else {
        console.warn('No market config found in shop metafield')
        this.marketConfig = null
        // Update debug panel
        if (this.debugMarketConfig) {
          this.debugMarketConfig.textContent = 'No config found in metafield'
        }
      }

      // Check if we have product data and return it
      if (json.data && json.data.product) {
        // Cache the result
        this.cache[cacheKey] = {
          timestamp: Date.now(),
          data: json.data.product,
        }
        return json.data.product
      } else if (json.errors) {
        // If there are errors, log them and return null
        console.error('GraphQL errors:', json.errors)
        return null
      } else {
        // If there's no product data and no errors, something else is wrong
        console.error('No product data in response and no errors reported')
        return null
      }
    } catch (error) {
      console.error('Failed to fetch product data:', error)
      return null
    }
  }

  /**
   * Find the current variant in the API response data
   * @param {Object} data - The API response data
   * @returns {Object|undefined} The current variant or undefined if not found
   */
  findCurrentVariant (data, currentVariantId) {
    return data.variants.edges.find(
      edge =>
        edge.node.id === `gid://shopify/ProductVariant/${currentVariantId}`,
    )?.node
  }

  /**
   * Maps and validates preorder pool data from variant metafield
   * @param {Object} variant - The current product variant
   * @returns {Array|null} Array of mapped pool objects or null if invalid
   */
  getPreorderPools (variant) {
    if (!variant.metafield?.value) return null

    try {
      const data = JSON.parse(variant.metafield.value)
      const pools = Array.isArray(data) ? data : [data]

      const mappedPools = pools.map(pool => ({
        enabled: pool.enabled ?? false, // Use pool's enabled value, fallback to false if not specified
        limit: pool.inventoryLimit || pool.limit || 0,
        reserved: pool.reserved || 0,
        sellingPlanGroupId: pool.sellingPlanGroupId,
        sellingPlanId: pool.sellingPlanId,
        productPageText: pool.productPageText || 'To be confirmed',
        estDispatchDate: pool.estDispatchDate,
        location: pool.location,
        availableForPreorder: pool.availableForPreorder || 0,
      }))
      return mappedPools
    } catch (error) {
      return null
    }
  }

  /**
   * Finds the appropriate preorder pool for the current market
   * Checks pool enabled status and available inventory
   * @param {Array} pools - Available preorder pools
   * @returns {Object|null} The matching pool for the current market
   */
  findMarketPool (pools) {
    // Early return with helpful message if no market config
    if (!this.hasValidMarketConfig()) {
      return null
    }

    const marketSettings = this.getMarketSettings()
    if (!marketSettings) {
      return null
    }

    return this.findPoolByPriorities(pools, marketSettings.poolPriorities)
  }

  /**
   * Checks if we have a valid market configuration
   * @returns {boolean} Whether the market config is valid
   */
  hasValidMarketConfig () {
    if (
      !this.marketConfig ||
      typeof this.marketConfig !== 'object' ||
      Object.keys(this.marketConfig).length === 0
    ) {
      console.warn(
        'No market configuration found. Preorder functionality will be disabled.',
      )
      return false
    }
    return true
  }

  /**
   * Gets the settings for the current market
   * @returns {Object|null} The market settings or null if not found
   */
  getMarketSettings () {
    // Find the market code that matches the current market handle
    const marketCode = Object.keys(this.marketConfig).find(
      code => this.marketConfig[code].marketHandle === this.currentMarket,
    )

    if (!marketCode) {
      console.warn(
        `Current market "${this.currentMarket}" not found in market configuration.`,
      )
      return null
    }

    return this.marketConfig[marketCode]
  }

  /**
   * Finds a pool based on the priority list
   * @param {Array} pools - Available preorder pools
   * @param {Array} priorities - Pool location priorities
   * @returns {Object|null} The matching pool or null if none found
   */
  findPoolByPriorities (pools, priorities) {
    if (!priorities || priorities.length === 0) {
      return null
    }

    // Try each location in priority order
    for (const locationId of priorities) {
      if (!locationId) continue

      const pool = this.findAvailablePoolByLocation(pools, locationId)
      if (pool) return pool
    }

    return null
  }

  /**
   * Finds an available pool for a specific location
   * @param {Array} pools - Available preorder pools
   * @param {string} locationId - The location ID to check
   * @returns {Object|null} The matching pool or null if none found
   */
  findAvailablePoolByLocation (pools, locationId) {
    // Filter pools by location, availability, and enabled status
    const locationPools = pools.filter(p => {
      return (
        p.location === locationId &&
        parseInt(p.availableForPreorder, 10) > 0 &&
        p.enabled === true
      )
    })

    if (locationPools.length === 0) return null

    // Sort pools by estDispatchDate (earliest first)
    const sortedPools = locationPools.sort((a, b) => {
      // If either date is missing, put it at the end
      if (!a.estDispatchDate) return 1
      if (!b.estDispatchDate) return -1

      // Parse dates and compare them
      const dateA = new Date(a.estDispatchDate)
      const dateB = new Date(b.estDispatchDate)

      // Sort ascending (earliest first)
      return dateA - dateB
    })

    return sortedPools[0]
  }

  /**
   * Format a date as MM-DD-YYYY
   * @param {Date} date - The date to format
   * @returns {string} The formatted date string
   */
  formatDate (date) {
    const month = date.toLocaleString('en-GB', { month: 'long' })
    const day = String(date.getDate())

    const nthNumber = number => {
      if (number > 3 && number < 21) return 'th'
      switch (number % 10) {
        case 1:
          return 'st'
        case 2:
          return 'nd'
        case 3:
          return 'rd'
        default:
          return 'th'
      }
    }
    return `${month} ${day}${nthNumber(day)}`
  }
}

window.PreBuyUtils = PreBuyUtils
